import React, { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import "./App.css";
import { useTranslation } from "react-i18next";
import i18n from "./i18n";

const Settings = ({ userId }) => {

  const [settings, setSettings] = useState({
    ic_mattino: "",
    ic_pomeriggio: "",
    ic_sera: "",
    fsi: "",
    glicemia_target: "",
    anno_scoperta: "",
    anno_nascita: "",
    citta: "",
    peso: "",
    altezza: "",
    device: "",
    centro_diabetologico: "",
    timezone: "" ,
    notif_bolo: false,
    notif_valutazione: false,
  });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  const isNativeCapacitor = () => {
    return !!(window?.Capacitor?.isNativePlatform?.() || window?.Capacitor?.isNative);
  };

  const [language, setLanguage] = useState(
    localStorage.getItem("appLanguage") || i18n.language || "it"
  );

  useEffect(() => {
    const saved = localStorage.getItem("appLanguage");
    const lang = saved || i18n.language || "it";
    setLanguage(lang);
    i18n.changeLanguage(lang);
  }, []);

  useEffect(() => {
    localStorage.setItem("appLanguage", language);
    i18n.changeLanguage(language);
  }, [language]);

  const timeZones =
    typeof Intl.supportedValuesOf === "function"
      ? Intl.supportedValuesOf("timeZone")
      : [
          "Europe/Rome",
          "Europe/London",
          "America/New_York",
          "America/Los_Angeles",
          "Asia/Tokyo",
          "Australia/Sydney"
        ];

  useEffect(() => {
    const fetchSettings = async () => {
      const { data, error } = await supabase
        .from("users")
        .select(
          "ic_mattino, ic_pomeriggio, ic_sera, fsi, glicemia_target, anno_scoperta, anno_nascita, citta, peso, altezza, device, centro_diabetologico, timezone, notif_bolo, notif_valutazione"
        )
        .eq("id", userId)
        .single();

      if (error) {
        console.error("Errore nel recupero dei parametri:", error.message);
        setLoading(false);
        return;
      }

      setSettings({
        ic_mattino: data.ic_mattino ?? "",
        ic_pomeriggio: data.ic_pomeriggio ?? "",
        ic_sera: data.ic_sera ?? "",
        fsi: data.fsi ?? "",
        glicemia_target: data.glicemia_target ?? "",
        anno_scoperta: data.anno_scoperta ?? "",
        anno_nascita: data.anno_nascita ?? "",
        citta: data.citta ?? "",
        peso: data.peso ?? "",
        altezza: data.altezza ?? "",
        device: data.device ?? "",
        centro_diabetologico: data.centro_diabetologico ?? "",
        timezone: data.timezone ?? "Europe/Rome",
        notif_bolo: data.notif_bolo ?? false,
        notif_valutazione: data.notif_valutazione ?? false,
      });

      setLoading(false);
    };

    if (userId) fetchSettings();
  }, [userId]);

  const handleLanguageChange = (e) => {
    const lang = e.target.value;
    setLanguage(lang);
    localStorage.setItem("appLanguage", lang);
    i18n.changeLanguage(lang);
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setSettings((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    const { error } = await supabase
      .from("users")
      .update({
        ic_mattino: settings.ic_mattino ? parseFloat(settings.ic_mattino) : null,
        ic_pomeriggio: settings.ic_pomeriggio ? parseFloat(settings.ic_pomeriggio) : null,
        ic_sera: settings.ic_sera ? parseFloat(settings.ic_sera) : null,
        fsi: settings.fsi ? parseFloat(settings.fsi) : null,
        glicemia_target: settings.glicemia_target ? parseFloat(settings.glicemia_target) : null,
        anno_scoperta: settings.anno_scoperta ? parseInt(settings.anno_scoperta) : null,
        anno_nascita: settings.anno_nascita ? parseInt(settings.anno_nascita) : null,
        citta: settings.citta,
        peso: settings.peso ? parseFloat(settings.peso) : null,
        altezza: settings.altezza ? parseFloat(settings.altezza) : null,
        device: settings.device,
        centro_diabetologico: settings.centro_diabetologico,
        timezone: settings.timezone,
        notif_bolo: !!settings.notif_bolo,
        notif_valutazione: !!settings.notif_valutazione,
      })
      .eq("id", userId);

    if (error) {
      console.error("Errore nell'aggiornamento:", error.message);
      setMessage(`❌${t("erroreSalvataggio")}`);
    } else {
      setMessage(`${t("impostazioniAggiornate")}`);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      window.location.href = "/"; 
    } catch (error) {
      console.error("Errore durante il logout:", error);
    }
  };

  const { t } = useTranslation();

  return (
    <div className="container">
      <h1 className="title center">{t("impostazioni")}</h1>

      <p className="instructions1 center">
        {t("ricordatiSempre")}{" "}
        <a
          href="https://www.carbozen.it/disclaimer"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("disclaimer")}
        </a>
        .
      </p>

      {loading ? (
        <p className="center" style={{ marginTop: "18px" }}>
          {t("caricamento")}
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="settings-form">

          {/* NOTIFICHE */}
          {isNativeCapacitor() && (
            <div className="card-section card-green notif-card">
              <h3 className="settings-subtitle">{t("notifiche")}</h3>

              <div className="toggle-row">
                <span className="toggle-text">{t("notifBoloRitardato")}</span>
                <label className="switch">
                  <input
                    type="checkbox"
                    name="notif_bolo"
                    checked={!!settings.notif_bolo}
                    onChange={handleChange}
                  />
                  <span className="slider" />
                </label>
              </div>

              <div className="toggle-row">
                <span className="toggle-text">{t("notifValutazione")}</span>
                <label className="switch">
                  <input
                    type="checkbox"
                    name="notif_valutazione"
                    checked={!!settings.notif_valutazione}
                    onChange={handleChange}
                  />
                  <span className="slider" />
                </label>
              </div>

              {/* ✅ Bottone salva anche qui */}
              <div className="center" style={{ marginTop: "18px" }}>
                <button type="submit" className="custom-button">
                  {t("salvaImpostazioni")}
                </button>
              </div>

              {/* ✅ Messaggio anche qui (opzionale, ma utile) */}
              {message && (
                <p className="center" style={{ marginTop: "12px" }}>
                  {message}
                </p>
              )}
            </div>
          )}


          {/* CARD PRINCIPALE IMPOSTAZIONI */}
          <div className="card-section card-green">

            {/* ===== Sezione: Parametri insulina ===== */}
            <h3 className="settings-subtitle"> {t("parametriInsulinaTitolo")}</h3>

            {/* IC: 3 colonne */}
            <div className="settings-row-3">
              <label className="form-label settings-mini-label">
                {t("icMattino")}
                <div className="input-wrapper input-wrapper-sm">
                  <input
                    type="number"
                    name="ic_mattino"
                    value={settings.ic_mattino}
                    onChange={handleChange}
                    className="glicemia-input2"
                    step="0.1"
                    required
                  />
                </div>
              </label>

              <label className="form-label settings-mini-label">
                {t("icPomeriggio")}
                <div className="input-wrapper input-wrapper-sm">
                  <input
                    type="number"
                    name="ic_pomeriggio"
                    value={settings.ic_pomeriggio}
                    onChange={handleChange}
                    className="glicemia-input2"
                    step="0.1"
                    required
                  />
                </div>
              </label>

              <label className="form-label settings-mini-label">
                {t("icSera")}
                <div className="input-wrapper input-wrapper-sm">
                  <input
                    type="number"
                    name="ic_sera"
                    value={settings.ic_sera}
                    onChange={handleChange}
                    className="glicemia-input2"
                    step="0.1"
                    required
                  />
                </div>
              </label>
            </div>

            {/* FSI + Target: 2 colonne */}
            <div className="settings-row-2">
              <label className="form-label settings-mini-label">
                {t("fsiValore")}
                <div className="input-wrapper input-wrapper-md">
                  <input
                    type="number"
                    name="fsi"
                    value={settings.fsi}
                    onChange={handleChange}
                    className="glicemia-input2"
                    step="0.1"
                    required
                  />
                </div>
              </label>

              <label className="form-label settings-mini-label">
                {t("glicemiaTarget2")}
                <div className="input-wrapper input-wrapper-md">
                  <input
                    type="number"
                    name="glicemia_target"
                    value={settings.glicemia_target}
                    onChange={handleChange}
                    className="glicemia-input2"
                    required
                  />
                </div>
              </label>
            </div>

            <div className="settings-divider" />

            {/* ===== Sezione: Profilo ===== */}
            <h3 className="settings-subtitle">{t("profiloTitolo")}</h3>

            <div className="settings-grid-1col">
              <label className="form-label">
                {t("annoScoperta")}
                <div className="input-wrapper">
                  <input
                    type="number"
                    name="anno_scoperta"
                    value={settings.anno_scoperta}
                    onChange={handleChange}
                    className="glicemia-input2"
                  />
                </div>
              </label>

              <label className="form-label">
                {t("annoNascita")}
                <div className="input-wrapper">
                  <input
                    type="number"
                    name="anno_nascita"
                    value={settings.anno_nascita}
                    onChange={handleChange}
                    className="glicemia-input2"
                  />
                </div>
              </label>

              <label className="form-label">
                {t("cittaLabel")}
                <div className="input-wrapper">
                  <input
                    type="text"
                    name="citta"
                    value={settings.citta}
                    onChange={handleChange}
                    className="glicemia-input2"
                  />
                </div>
              </label>

              <label className="form-label">
                {t("pesoLabel")}
                <div className="input-wrapper">
                  <input
                    type="number"
                    name="peso"
                    value={settings.peso}
                    onChange={handleChange}
                    className="glicemia-input2"
                    step="0.1"
                  />
                </div>
              </label>

              <label className="form-label">
                {t("altezzaLabel")}
                <div className="input-wrapper">
                  <input
                    type="number"
                    name="altezza"
                    value={settings.altezza}
                    onChange={handleChange}
                    className="glicemia-input2"
                    step="0.1"
                  />
                </div>
              </label>

              <label className="form-label">
                {t("deviceUsato")}
                <div className="input-wrapper">
                  <input
                    type="text"
                    name="device"
                    value={settings.device}
                    onChange={handleChange}
                    className="glicemia-input2"
                  />
                </div>
              </label>

              <label className="form-label">
                {t("centroDiabetologico")}
                <div className="input-wrapper">
                  <input
                    type="text"
                    name="centro_diabetologico"
                    value={settings.centro_diabetologico}
                    onChange={handleChange}
                    className="glicemia-input2"
                  />
                </div>
              </label>

              <label className="form-label">
                {t("fusoOrario")}
                <div className="input-wrapper">
                  <select
                    name="timezone"
                    value={settings.timezone}
                    onChange={handleChange}
                    className="glicemia-input2"
                    required
                  >
                    {timeZones.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </select>
                </div>
              </label>
              <label className="form-label">
                {t("lingua")}
                <div className="input-wrapper">
                  <select
                    value={language}
                    onChange={handleLanguageChange}   // ✅ usa la funzione dello step 1
                    className="glicemia-input2"
                  >
                    <option value="it">🇮🇹 Italiano</option>
                    <option value="en">🇬🇧 English</option>
                    <option value="es">🇪🇸 Español</option>
                  </select>
                </div>
              </label>
            </div>

            <div className="center" style={{ marginTop: "18px" }}>
              <button type="submit" className="custom-button">
                {t("salvaImpostazioni")}
              </button>
            </div>

            {message && (
              <p className="center" style={{ marginTop: "12px" }}>
                {message}
              </p>
            )}
          </div>

          <div className="card-section card-green settings-logout-card">
            <div className="center">
              <div
                className="logout-inset"
                onClick={handleLogout}
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  (e.key === "Enter" || e.key === " ") && handleLogout()
                }
                aria-label={t("logout")}
              >
                {t("logout")}
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );

};

export default Settings;
